import { Integrations, SourceMiddleware } from '@static/js/types';
import { ConsentCategory, ConsentState } from './polarisConsent';
import { consentLogger } from './consentLogger';

/**
 * Segment integration configuration value
 * - boolean: Enable (true) or disable (false) the integration
 * - object: Enable with integration-specific options
 */
export type SegmentIntegrationValue = boolean | Record<string, unknown>;

/**
 * Segment integrations configuration object
 */
export type SegmentIntegrationsConfig = Readonly<Partial<Record<Integrations, SegmentIntegrationValue>>>;

/**
 * Integration configuration structure
 */
export interface IntegrationConfig {
  /** Whether this integration is enabled (false = always disabled regardless of consent) */
  enabled: boolean;
  /** Consent categories required for this integration (undefined = no consent required) */
  consent?: ReadonlyArray<ConsentCategory>;
  /** Integration-specific options (e.g., apiHost for SegmentIO) */
  options?: Record<string, unknown>;
}

/**
 * Configuration mapping type for Segment integrations
 */
type IntegrationConsentConfig = {
  [K in Integrations]?: IntegrationConfig;
};

/**
 * Configuration mapping Segment integrations to their requirements
 *
 * Segment loads every device-mode destination this map leaves out, and analytics.js copies the
 * resulting `integrations` booleans into every event, where Segment uses them to route events to
 * cloud-mode destinations. So a destination that needs consent must be named here, whether it
 * runs in the browser or on Segment's servers. Defaulting to `All: false` instead is not an
 * option: it would also switch off every unlisted cloud-mode destination (Iterable, Salesforce,
 * Slack, S3) for every visitor.
 */
export const INTEGRATION_CONSENT_CONFIG: IntegrationConsentConfig = {
  // Core Segment with custom API host - always enabled
  [Integrations.SegmentIO]: {
    enabled: true,
    options: {
      apiHost: 'p.withjoy.com/segapi/v1',
    },
  },

  // Always disabled integrations
  [Integrations.Intercom]: {
    enabled: false,
  },
  [Integrations.Chameleon]: {
    enabled: false,
  },

  // Statistics consent required
  [Integrations.GoogleAnalytics4Web]: {
    enabled: true,
    consent: ['statistics'],
  },
  [Integrations.Amplitude]: {
    enabled: true,
    consent: ['statistics'],
  },
  // Device mode in the production Segment source: Segment's tool for defining click events in its UI.
  [Integrations.VisualTagger]: {
    enabled: true,
    consent: ['statistics'],
  },

  // Marketing consent required
  [Integrations.FacebookPixel]: {
    enabled: true,
    consent: ['marketing'],
  },
  [Integrations.BingAds]: {
    enabled: true,
    consent: ['marketing'],
  },
  [Integrations.Criteo]: {
    enabled: true,
    consent: ['marketing'],
  },
  // Enabled in the production Segment source as a device-mode destination. Segment loads any
  // destination this map leaves out, so it must be listed to be gated.
  [Integrations.PinterestTag]: {
    enabled: true,
    consent: ['marketing'],
  },
  // Cloud mode: Segment forwards events to these from its servers, so the per-event
  // `integrations` flag is the only thing that keeps declined visitors' events out of them.
  [Integrations.FacebookConversionsApi]: {
    enabled: true,
    consent: ['marketing'],
  },
  [Integrations.OpenAIAdsConnector]: {
    enabled: true,
    consent: ['marketing'],
  },
} as const;

/**
 * Build Segment integrations config based on consent state
 *
 * This function iterates through all configured integrations and determines
 * which should be enabled based on the user's consent preferences.
 *
 * @param consent - The current user consent state for all categories
 * @returns Segment integrations configuration object with enabled/disabled integrations
 *
 * @example
 * ```typescript
 * const config = buildIntegrationsConfig({
 *   essential: true,
 *   statistics: true,
 *   marketing: false
 * });
 * // Returns: { 'Segment.io': { apiHost: '...' }, 'Google Analytics 4 Web': true, ... }
 * ```
 */
export function buildIntegrationsConfig(consent: ConsentState): SegmentIntegrationsConfig {
  const integrations: Partial<Record<Integrations, SegmentIntegrationValue>> = {};

  // Use typed keys for better type safety
  const integrationKeys = Object.keys(INTEGRATION_CONSENT_CONFIG) as Integrations[];

  for (const integration of integrationKeys) {
    const config = INTEGRATION_CONSENT_CONFIG[integration];
    if (!config) continue;

    try {
      // Always disabled integrations
      if (!config.enabled) {
        integrations[integration] = false;
        continue;
      }

      // Check if consent requirements are met
      if (config.consent && config.consent.length > 0) {
        const hasConsent = config.consent.every((category) => consent[category]);

        if (!hasConsent) {
          integrations[integration] = false;
          continue;
        }
      }

      // Integration is enabled - return options if provided, otherwise true
      integrations[integration] = config.options || true;
    } catch (error) {
      consentLogger.error(`Error processing integration ${integration}:`, error);
      // Fail safe: disable integration on error
      integrations[integration] = false;
    }
  }

  return integrations;
}

/**
 * Build a Segment source middleware that keeps every destination the visitor's consent switched
 * off switched off, whatever an event's own options say.
 *
 * Call sites pass per-event integrations such as `{ 'Actions Amplitude': true }`, and analytics.js
 * merges them over the load-time flags, so without this an event could reach a destination the
 * visitor declined. The middleware runs on every event before it is sent and puts each `false`
 * flag back.
 *
 * @param integrations - The consent-based config passed to `analytics.load`
 * @returns The middleware to register with `analytics.addSourceMiddleware`
 */
export function enforceConsentMiddleware(integrations: SegmentIntegrationsConfig): SourceMiddleware {
  const switchedOff = (Object.keys(integrations) as Integrations[])
    .filter((integration) => integrations[integration] === false)
    .reduce<Record<string, false>>((flags, integration) => ({ ...flags, [integration]: false }), {});

  return ({ payload, next }) => {
    payload.obj.integrations = { ...payload.obj.integrations, ...switchedOff };
    next(payload);
  };
}
